/**
 * KDS Slice
 * Redux slice for Kitchen Display Screen state — selected department and
 * mute preference persist to localStorage, mirroring outletSlice.ts's
 * setSelectedOutletId pattern.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  fetchKdsBoard,
  fetchKdsDepartments,
  transitionKdsTicket,
} from './kdsThunks';
import { KdsState } from './kdsTypes';

const KDS_DEPARTMENT_STORAGE_KEY = 'kds_selected_department_id';
const KDS_MUTE_STORAGE_KEY = 'kds_sound_muted';

const readStoredDepartmentId = (): string | null => {
  try {
    return JSON.parse(
      localStorage.getItem(KDS_DEPARTMENT_STORAGE_KEY) || 'null',
    );
  } catch {
    return null;
  }
};

const readStoredMuted = (): boolean => {
  try {
    return JSON.parse(localStorage.getItem(KDS_MUTE_STORAGE_KEY) || 'false');
  } catch {
    return false;
  }
};

const initialState: KdsState = {
  selectedDepartmentId: readStoredDepartmentId(),
  isMuted: readStoredMuted(),
  departments: [],
  tickets: [],
  kpis: {
    active_orders: 0,
    average_time_minutes: 0,
    completed_orders: 0,
    delayed_orders: 0,
  },
  isLoading: false,
  error: null,
  pendingTransitions: {},
};

const kdsSlice = createSlice({
  name: 'kds',
  initialState,
  reducers: {
    setSelectedDepartmentId: (state, action: PayloadAction<string | null>) => {
      state.selectedDepartmentId = action.payload;
      localStorage.setItem(
        KDS_DEPARTMENT_STORAGE_KEY,
        JSON.stringify(action.payload),
      );
    },
    toggleKdsMuted: (state) => {
      state.isMuted = !state.isMuted;
      localStorage.setItem(KDS_MUTE_STORAGE_KEY, JSON.stringify(state.isMuted));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKdsBoard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchKdsBoard.fulfilled, (state, action) => {
        state.isLoading = false;
        // Don't let a fetch that raced an in-flight optimistic transition
        // (see transitionKdsTicket.pending below) show a ticket's status as
        // behind where the user already moved it — regardless of which of
        // the several fetch triggers (poll, PowerSync change, Realtime,
        // the transition thunk's own resync) this happens to be, or the
        // order they resolve in. This is what stops the "moves → snaps back
        // → moves again" flicker.
        state.tickets = action.payload.tickets.map((ticket) => {
          const pendingStatus = state.pendingTransitions[ticket.id];
          return pendingStatus && pendingStatus !== ticket.status
            ? { ...ticket, status: pendingStatus }
            : ticket;
        });
        state.kpis = action.payload.kpis;
      })
      .addCase(fetchKdsBoard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchKdsDepartments.fulfilled, (state, action) => {
        state.departments = action.payload;
      })
      .addCase(fetchKdsDepartments.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Optimistic move: reflect the new status immediately on click, rather
    // than waiting for the local SQLite write + re-fetch round-trip. Since
    // KdsBoard filters strictly by status per column (and SERVED matches no
    // column), this alone makes the card jump columns — or leave the board
    // entirely — right away. transitionKdsTicket resyncs from real SQLite
    // state via fetchKdsBoard on both success and failure, so a failed write
    // self-corrects instead of leaving the card stuck in the wrong column.
    builder
      .addCase(transitionKdsTicket.pending, (state, action) => {
        const { ticketId, toStatus } = action.meta.arg;
        const ticket = state.tickets.find((t) => t.id === ticketId);
        if (ticket) {
          ticket.status = toStatus;
        }
        // Guarded until this exact transition attempt settles (fulfilled or
        // rejected below) — see fetchKdsBoard.fulfilled above for why.
        state.pendingTransitions[ticketId] = toStatus;
      })
      .addCase(transitionKdsTicket.fulfilled, (state, action) => {
        delete state.pendingTransitions[action.meta.arg.ticketId];
      })
      .addCase(transitionKdsTicket.rejected, (state, action) => {
        delete state.pendingTransitions[action.meta.arg.ticketId];
      });
  },
});

export const { setSelectedDepartmentId, toggleKdsMuted } = kdsSlice.actions;
export default kdsSlice.reducer;
